import { useMutation, useQueryClient } from "@tanstack/react-query"

import { supabase } from "@/lib/supabase"
import { getFunctionsBaseUrl } from "@/utils/getFunctionsBaseUrl"

export interface CreateTrackPodAccountRequest {
  peopleId: string
}

export interface CreateTrackPodAccountResponse {
  success: boolean
  status: "created" | "skipped_already_exists"
  username: string | null
  error?: string
}

/**
 * Manually create a Track-POD driver account for a person. Calls the people edge
 * function's /create-trackpod-account route.
 */
export function useCreateTrackPodAccount() {
  const queryClient = useQueryClient()

  return useMutation<CreateTrackPodAccountResponse, Error, CreateTrackPodAccountRequest>({
    mutationFn: async (request) => {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token

      if (!token) {
        throw new Error("No authentication token available")
      }

      const functionUrl = `${getFunctionsBaseUrl()}/people/create-trackpod-account`

      const response = await fetch(functionUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(request),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(
          errorData.error || `Failed to create Track-POD account: ${response.statusText}`,
        )
      }

      const data: CreateTrackPodAccountResponse = await response.json()

      if (!data.success) {
        throw new Error(data.error || "Failed to create Track-POD account")
      }

      return data
    },
    onSuccess: (_, request) => {
      queryClient.invalidateQueries({ queryKey: ["people"] })
      queryClient.invalidateQueries({ queryKey: ["person", request.peopleId] })
    },
  })
}
